import {portrait} from './shared.js';

const narrativeBGLight = 'rgba(0, 0, 0, 0.0)';
const narrativeBGDarken = 'rgba(0, 0, 0, 0.3)';

/**
 * Set up a narrative timeline
 * @param {TimelineLite} timeline timeline
 * @param {Array} narrativeText array of text <div>s
 * @param {Number} wait initial wait at beginning of timeline
 * @param {String} dark narrative background dark color
 * @param {String} light narrative background light color
 */
export function buildNarrativeTextTimeline(timeline, narrativeText, wait, dark = narrativeBGDarken, light = narrativeBGLight) {
  timeline.to({}, wait, {});

  for (const text of narrativeText.querySelectorAll('div')) {
    const content = Array.from(text.children);

    TweenLite.set(content, {opacity: 0, transform: 'translateY(5.5vh)'});
    TweenLite.set(text, {backgroundColor: light});

    timeline.set(content, {opacity: 0, transform: 'translateY(5.5vh)'});
    timeline.set(text, {backgroundColor: light});

    timeline.add([
      TweenLite.to(content, 50, {opacity: 1, transform: 'translateY(0vh)', ease: Sine.easeOut}),
      TweenLite.to(text, 50, {backgroundColor: dark}),
    ]);

    timeline.add([
      TweenLite.to(content, 25, {opacity: 0, transform: 'translateY(-11vh)', ease: Sine.easeIn}),
      TweenLite.to(text, 25, {backgroundColor: light}),
    ]);
  }
}

/**
 * Draw a frame from a video on a canvas
 * @param {HTMLVideoElement} video video
 * @param {*} context canvas context
 */
export function drawFrame(video, context) {
  if (video.readyState <= video.HAVE_CURRENT_DATA) {
    return;
  }

  const canvas = context.canvas;
  const canvasRatio = canvas.width / canvas.height;
  const videoRatio = video.videoWidth / video.videoHeight;
  let [width, height] = (canvasRatio <= videoRatio) ? [canvas.height * videoRatio, canvas.height] : [canvas.width, canvas.width / videoRatio];
  // compute top/left coordinates to start drawing video frame so that it is centered on canvas;
  // top/left are positive or negative depending on canvas aspect ratio
  let [top, left] = [(canvas.height - height) / 2, (canvas.width - width) / 2];
  context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight, left, top, width, height);
}

const timePerLine = 50;
const lineLength = 700;

/**
 * Animate pages on a timeline
 * @param {Iterable} pages pages
 * @param {TimelineLite} timeline timeline
 * @param {Boolean} slide slide
 */
export function animatePages(pages, timeline, slide = true) {
  for (const page of pages) {
    // simultaneously fade in and scale down the page, with negative time offset to overlap with previous page slide animation
    timeline.add([
      TweenLite.to(page, 5, {opacity: 1}),
      TweenLite.to(page, 25, {scale: 1}),
    ], '-=25');

    timeline.to({}, 10, {});

    // animate any highlight paths on the page
    for (const path of page.querySelectorAll('svg path')) {
      const length = Math.ceil(path.getTotalLength());
      const time = timePerLine * (length / lineLength);
      timeline.to(path, time, {'stroke-dashoffset': 0});
    }

    timeline.to({}, 10, {});

    // if the page has slide properties (to move it around after it 'lands'), animate that, otherwise just insert a delay
    if (page.dataset.slideProp && slide) {
      timeline.to(page, 25, {[page.dataset.slideProp]: page.dataset.slideVal});
    } else {
      timeline.to({}, 25, {});
    }
  }
}

/**
 * Animate highlights
 * @param {Array} highlights highlights
 * @param {TimelineLite} timeline timeline
 */
export function animateTextHighlights(highlights, timeline) {
  for (const highlight of highlights) {
    // get the total length of all the lines of the highlight
    const length = Array.from(highlight.getClientRects()).reduce((length, {width}) => length + width, 0);

    // vary the time of the animation by the total length of the highlight
    const time = timePerLine * (length / lineLength);

    // animate from/to depending on type of highlight (redacted 'from', regular 'to')
    if (highlight.classList.contains('redacted')) {
      timeline.to(highlight, time, {backgroundPosition: '101% 0%'});
    } else {
      timeline.to(highlight, time, {backgroundPosition: '0% 0%'});
    }
  }
}

/**
 * Set up highlight paths to be animated
 * @param {Iterable} paths paths
 */
export function dashPaths(paths) {
  for (const path of paths) {
    const length = Math.ceil(path.getTotalLength());
    TweenLite.set(path, {attr: {'stroke-dasharray': `${length} ${length}`, 'stroke-dashoffset': length}});
  }
}

/**
 * Animate callout text
 * @param {DOMNode} callout callout
 * @param {TimelineLite} timeline timeline
 */
export function animateCallout(callout, timeline) {
  const content = callout.querySelector('p span');
  // fade in the callout, then animate the text (using GreenSock TextPlugin)
  timeline.to(callout, 25, {opacity: 1, x: '0%'});
  timeline.to(content, 50, {text: content.dataset.text});
}

/**
 * Get year data from <g>
 * @param {*} year <g>
 * @return {Object} year, dead, injured
 */
export function getYearState(year) {
  return [...year.classList].reduce((state, className) => {
    if (/year-(\d+)/.test(className)) {
      const [, year] = className.match(/year-(\d+)/);
      state.year = +year;
    } else if (/dead-(\d+)/.test(className)) {
      const [, dead] = className.match(/dead-(\d+)/);
      state.dead = +dead;
    } else if (/injured-(\d+)/.test(className)) {
      const [, injured] = className.match(/injured-(\d+)/);
      state.injured = +injured;
    }

    return state;
  }, {});
}

/**
 * Create a flipbook animation from a flipbook container's data attributes
 * @param {DOMNode} flipbook flipbook container with data attributes
 * @param {Number} prefix prefix of each flipbook image
 */
export async function createFlipbook(flipbook, prefix) {
  flipbook.classList.add('loading');

  const count = portrait.matches ? flipbook.dataset.countPortrait : flipbook.dataset.countLandscape;

  let remaining = count;

  while (flipbook.children.length) {
    flipbook.firstChild.remove();
  }

  for (let n = 0; n < 5; n++) {
    for (let i = n; i < count; i += 5) {
      const path = `${flipbook.dataset.path}/${portrait.matches ? 'portrait' : 'landscape'}/${String(prefix) + String(i).padStart(2, '0')}.jpg`;

      const li = document.createElement('li');
      li.dataset.seq = i;

      const image = document.createElement('img');

      image.src = path;

      li.appendChild(image);

      const next = Array.from(flipbook.children).find(li => li.dataset.seq > i);
      if (next) {
        next.insertAdjacentElement('beforebegin', li);
      } else {
        flipbook.appendChild(li);
      }

      await new Promise(resolve => {
        image.addEventListener('load', () => resolve(), {once: true});
        image.addEventListener('error', () => resolve(), {once: true});
      });

      li.classList.add('loaded');
      if (--remaining) {
        flipbook.classList.remove('loading');
      }
    }
  }
}

/**
 * Scrub a flipbook animation
 * @param {DOMNode} flipbook flipbook container - each direct child is a flip frame
 * @param {Number} percent progress through the flipbook
 */
export function scrubFlipbook(flipbook, percent) {
  let n = Math.trunc(percent * (flipbook.children.length - 1));

  for (const frame of flipbook.children) {
    frame.classList.remove('current');
  }

  let frame = flipbook.children[n];

  if (frame && frame.classList.contains('loaded')) {
    frame.classList.add('current');
  } else {
    // search for the nearest earlier loaded frame to show (searching both directions causes flickering)
    while (--n >= 0) {
      frame = flipbook.children[n];
      if (frame && frame.classList.contains('loaded')) {
        frame.classList.add('current');
        break;
      }
    }
  }
}
